import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { format, parseISO } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import { getBundlePurchases, getManualAdjustments } from "../API/companyApi";
import css from "./css/PurchaseTable.module.css";
import ClaimGuard from "../components/Auth/ClaimGuard";
import { GET_BILLING_REPORT_ALL } from "../Claims";
import { useTranslation, Trans } from "react-i18next";

const PurchaseTable = ({ companyId, type , refreshTrigger}) => {
  const [startDate, setStartDate] = useState(new Date("2025-05-01"));
  const [endDate, setEndDate] = useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
  });

  const [data, setData] = useState([]);
  const { t } = useTranslation();

  useEffect(() => {
    if (!startDate || !endDate) return;

    async function fetchData() {
      const range = `${format(startDate, "yyyy-MM-dd")}|${format(
        endDate,
        "yyyy-MM-dd"
      )}`;
      try {
        if (type == "adjusted_balance") {
          const response = await getManualAdjustments(companyId, range);
          const adjustments = response.data;
          const formatted = adjustments.map((item) => ({
            date: format(parseISO(item.timestamp), "dd MMM yyyy, HH:mm"),
            email: item.issuer,
            amount: item.amount,
            afterModification: item.balanceAfterModification,
            note: item.note?.trim() ? item.note : "—",
          }));
          setData(formatted);
        } else if (type == "purchase_table") {
          const response = await getBundlePurchases(companyId, range);
          const purchases = response.data;
          const formatted = purchases.map((item) => ({
            date: format(parseISO(item.purchaseDate), "dd MMM yyyy, HH:mm"),
            email: item.purchasedBy,
            amount: item.amount,
            balanceBeforePurchase: item.balanceBeforePurchase,
            balanceAfterPurchase: item.balanceAfterPurchase,
            note: item.note?.trim() ? item.note : "—",
          }));

          setData(formatted);
        }
      } catch (error) {
        console.error("Failed to fetch purchase data:", error);
      }
    }

    fetchData();
  }, [startDate, endDate, companyId, refreshTrigger]);

  return (
    <div className={css["purchase-chart-card"]}>
      <div className={css["purchase-chart-header"]}>
        <h3>
          {type == "purchase_table"
            ? t("creditManagement.purchaseHistory.title")
            : t("creditManagement.adjustedBalanceTable")}
        </h3>
        <div className={css["date-picker-row"]}>
          <div className={css["date-picker-group"]}>
            <label>{t("creditManagement.startDate")}</label>
            <DatePicker
              selected={startDate}
              onChange={(date) => date && setStartDate(date)}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              dateFormat="yyyy-MM-dd"
              className={css["styled-datepicker"]}
            />
          </div>
          <div className={css["date-picker-group"]}>
            <label>{t("creditManagement.endDate")}</label>
            <DatePicker
              selected={endDate}
              onChange={(date) => date && setEndDate(date)}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
              dateFormat="yyyy-MM-dd"
              className={css["styled-datepicker"]}
            />
          </div>
        </div>
      </div>

      <div className={css["purchase-chart-body"]}>
        {data.length === 0 ? (
          <p>{t("creditManagement.purchaseHistory.noData")}</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>{t("creditManagement.purchaseHistory.date")}</th>
                <th style={{ width: "25%" }}>
                  {t("creditManagement.purchaseHistory.emailOfBuyer")}
                </th>
                <th>{t("creditManagement.purchaseHistory.amount")}</th>
                {type == "purchase_table" ? (
                  <>
                    <th>
                      {t(
                        "creditManagement.purchaseHistory.balanceBeforePurchase"
                      )}
                    </th>
                    <th>
                      {t(
                        "creditManagement.purchaseHistory.balanceAfterPurchase"
                      )}
                    </th>
                  </>
                ) : (
                  <th>
                   {t(
                        "creditManagement.purchaseHistory.afterModification"
                      )}
                  </th>
                )}
                <ClaimGuard requiredClaim={GET_BILLING_REPORT_ALL}>
                  <th>{t("creditManagement.purchaseHistory.note")}</th>
                </ClaimGuard>
              </tr>
            </thead>
            <tbody>
              {data.map((row, idx) => (
                <tr key={idx}>
                  <td>{row.date}</td>
                  <td style={{ width: "25%" }}>{row.email}</td>
                  <td>{row.amount}</td>
                  {type == "purchase_table" ? (
                    <>
                      <td>{row.balanceBeforePurchase}</td>
                      <td>{row.balanceAfterPurchase}</td>
                    </>
                  ) : (
                    <>
                    <td>{row.afterModification}</td>
                    </>
                  )}
                  <ClaimGuard requiredClaim={GET_BILLING_REPORT_ALL}>
                    <td>{row.note}</td>
                  </ClaimGuard>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default PurchaseTable;
